// @ts-nocheck
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
// import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand"; // Starter Custom Code
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
// import Typography, { TypographyProps } from "@insite/mobius/Typography"; // Foundation
import * as React from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import ProductBrand, { ProductBrandStyles } from "@store/Overrides/Components/ProductBrand";
import { typographyTitleStyles } from "@store/Themes/common/mixins/typography";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
/* Starter Custom Code - End */

// Foundation -->
import useParentProductState from "@store/Framework/Store/Foundation/Data/Products/ProductsHooks/useParentProductState";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
// <-- foundation

type Props = WidgetProps & HasProduct;

export interface ProductDetailsPageTitleStyles {
    container?: GridContainerProps;
    brandGridItem?: GridItemProps;
    brandStyles?: ProductBrandStyles;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
}

// Foundation --->
const brandNameText: TypographyProps = {
    css: css`
        width: 100%;
        ${wrapInContainerStyles}
        font-size: ${({ theme }) => theme.design.typography.size.smaller};

        ${({ theme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        margin-bottom: 0.5em;
                    `,
                ],
                mediaOptions.max,
            )}
    `,
};
// <--- Foundation

export const pageTitleStyles: ProductDetailsPageTitleStyles = {
    container: {
        gap: 0,
        css: css`
            ${spacingStyles(null, pxToRem(12))}
        `,
    },
    brandGridItem: {
        width: 12,
    },
    brandStyles: {
        logoImage: {
            css: css`
                img {
                    max-width: ${pxToRem(150)};
                    max-height: ${pxToRem(150)};
                }
            `,
        },
        nameText: brandNameText,
        nameLink: {
            css: css`
                width: 100%;
            `,
            typographyProps: brandNameText,
        },
    },
    titleGridItem: {
        width: 12,
    },
    titleText: {
        forwardAs: "h1",
        color: "primary",
        css: css`
            ${typographyTitleStyles(pxToRemArray([30, null, 32, 36]))}
            ${breakpointValues(pxToRemArray([34, null, 36, 40]), "line-height")}
            font-weight: ${({ theme }) => theme.design.typography.weight.medium};
        `,
    },
};

const styles = pageTitleStyles;

const ProductDetailsPageTitle: React.FC<Props> = ({ product }) => {
    // Foundation --->
    const parentProductState = useParentProductState();

    const titleTextStyles: TypographyPresentationProps = {
        ...styles.titleText,
        forwardAs: parentProductState?.productTitle !== product.productTitle ? "h2" : "h1",
        css: css`
            ${styles.titleText?.css}
            margin-top: 0;
            margin-bottom: 0;
        `,
    };
    // <--- Foundation

    return (
        <GridContainer {...styles.container}>
            {product.brand && (
                <GridItem {...styles.brandGridItem}>
                    <ProductBrand brand={product.brand} showLogo={true} extendedStyles={styles.brandStyles} />
                </GridItem>
            )}
            <GridItem {...styles.titleGridItem}>
                {/* Foundation ---> */}
                {parentProductState?.productTitle !== product.productTitle && (
                    <VisuallyHidden>
                        <Typography forwardAs="h1">{parentProductState?.productTitle}</Typography>
                    </VisuallyHidden>
                )}
                {/* <--- Foundation */}
                <Typography {...titleTextStyles /* Foundation */} data-test-selector="ProductDetailsPageTitle">
                    {product.productTitle}
                </Typography>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsPageTitle),
    definition: {
        displayName: "Page Title",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
